// usePreviews.ts
import { useEffect, useMemo, useState } from 'react';
import { ExistingPreview, PreviewItem } from '../components/commetFileModal';
import { isNewPreview } from '../utils';


export type ServerFile = { name: string; size: number; type: string; url: string };

export function usePreviews(
  serverFiles: ServerFile[] | null | undefined,
  makeAbs: (rel?: string) => string,
) {
  const [previews, setPreviews] = useState<PreviewItem[]>([]);

  useEffect(() => {
    const mapped: ExistingPreview[] = (serverFiles ?? []).map((f) => ({
      kind: 'existing',
      url: makeAbs(f.url),
      name: f.name,
      size: f.size,
      type: f.type,
    }));
    setPreviews(mapped);
  }, [serverFiles, makeAbs]);

  // load dimensions once per preview
  useEffect(() => {
    let cancelled = false;
    previews.forEach((p) => {
      if (p.w && p.h) return;
      const img = new window.Image();
      img.onload = () => {
        if (cancelled) return;
        setPreviews((prev) => {
          const ix = prev.findIndex((x) => x.url === p.url);
          if (ix === -1) return prev;
          const copy = [...prev];
          copy[ix] = { ...copy[ix], w: img.naturalWidth || 1600, h: img.naturalHeight || 1200 };
          return copy;
        });
      };
      img.src = p.url;
    });
    return () => {
      cancelled = true;
    };
  }, [previews]);

  // revoke blobs on unmount
  useEffect(() => {
    return () => {
      previews.forEach((p) => {
        if (isNewPreview(p)) URL.revokeObjectURL(p.url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // exposed helpers
  const addFiles = (files: File[]) => {
    const news: PreviewItem[] = files.map((file) => ({
      kind: 'new',
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      type: file.type,
    }));
    setPreviews((prev) => [...prev, ...news]);
  };

  const removePreview = (p: PreviewItem) => {
    if (isNewPreview(p)) URL.revokeObjectURL(p.url);
    setPreviews((prev) => prev.filter((x) => x !== p));
  };

  const clearNonExisting = () => setPreviews((prev) => prev.filter((p) => p.kind === 'existing'));

  const newFiles = useMemo(() => previews.filter(isNewPreview).map((p) => p.file), [previews]);

  return { previews, setPreviews, addFiles, removePreview, clearNonExisting, newFiles };
}
